import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { ModalContainer } from '@containers/layout';
import { SubHeading, Box, Button } from '@components/common';
import BookLinkButton from '@components/BookLinkButton';
import DialogBox from '@components/DialogBox';
import { CleansSelectors } from '@redux/CleansRedux';
import BookingActions from '@redux/BookingRedux';
import ScheduleActions from '@redux/ScheduleRedux';
import history from '@utils/history';
import { trackSetRecuringService } from '@services/analytics';
import { BOOKING_STAGE, FREQUENCIES } from '@services/minte';

const RESCHEDULE_ITEMS = {
  ONCE: 'once',
  WEEK: 'week',
  BIWEEK: 'biweek',
  FOURWEEK: 'fourweek',
  CUSTOM: 'custom'
};

const StyledButton = styled(Button)`
  display: flex;
  flex-direciton: row;
  justify-content: flex-start;
`;

function Reschedule(props) {
  const {
    show,
    dismiss,
    currentClean,
    rescheduleSingleBooking,
    rescheduleRecurringBooking,
    rescheduleCustom
  } = props;
  const [selected, setSelected] = useState('');

  const onPressItem = item => {
    setSelected(item);
  };

  const {
    momentServiceDate,
    serviceType: serviceTypeInfo,
    additionalService
  } = currentClean;
  const serviceType = serviceTypeInfo.name;
  const addons = additionalService.map(addon => addon.name);
  const recurringCol = momentServiceDate.day() - 1;

  useEffect(() => {
    switch (selected) {
      case RESCHEDULE_ITEMS.ONCE:
        rescheduleSingleBooking({ serviceType, addons });
        break;
      case RESCHEDULE_ITEMS.WEEK:
        rescheduleRecurringBooking({
          rows: [0, 1, 2, 3],
          col: recurringCol,
          serviceType,
          addons
        });
        break;
      case RESCHEDULE_ITEMS.BIWEEK:
        rescheduleRecurringBooking({
          rows: [0, 2],
          col: recurringCol,
          serviceType,
          addons
        });
        break;
      case RESCHEDULE_ITEMS.FOURWEEK:
        rescheduleRecurringBooking({
          rows: [3],
          col: recurringCol,
          serviceType,
          addons
        });
        break;
      case RESCHEDULE_ITEMS.CUSTOM:
        rescheduleCustom();
        break;
    }

    if (selected) {
      trackSetRecuringService(selected, serviceType, addons);
      history.push('/book');
    }
  }, [selected]);

  return (
    <DialogBox show={show} onClickOutside={dismiss}>
      <ModalContainer>
        <Box display="flex" flexDirection="column">
          <SubHeading textAlign="center" fontWeight="bold" mt={4} mb={4}>
            How often would you like to receive this service?
          </SubHeading>
          <StyledButton
            mb={4}
            display="flex"
            justifyContent="flex-start"
            onClick={() => {
              onPressItem(RESCHEDULE_ITEMS.ONCE);
            }}
            variant={
              selected === RESCHEDULE_ITEMS.ONCE ? 'primary' : 'secondary'
            }
          >
            Once
          </StyledButton>
          <StyledButton
            mb={4}
            display="flex"
            justifyContent="flex-start"
            onClick={() => {
              onPressItem(RESCHEDULE_ITEMS.WEEK);
            }}
            variant={
              selected === RESCHEDULE_ITEMS.WEEK ? 'primary' : 'secondary'
            }
          >
            Weekly
          </StyledButton>
          <StyledButton
            mb={4}
            display="flex"
            justifyContent="flex-start"
            onClick={() => {
              onPressItem(RESCHEDULE_ITEMS.BIWEEK);
            }}
            variant={
              selected === RESCHEDULE_ITEMS.BIWEEK ? 'primary' : 'secondary'
            }
          >
            Every 2 weeks
          </StyledButton>
          <StyledButton
            mb={4}
            display="flex"
            justifyContent="flex-start"
            onClick={() => {
              onPressItem(RESCHEDULE_ITEMS.FOURWEEK);
            }}
            variant={
              selected === RESCHEDULE_ITEMS.FOURWEEK ? 'primary' : 'secondary'
            }
          >
            Every 4 weeks
          </StyledButton>
          <BookLinkButton
            title="Custom"
            variant={
              selected === RESCHEDULE_ITEMS.CUSTOM ? 'primary' : 'secondary'
            }
            mb={4}
            onClick={() => {
              onPressItem(RESCHEDULE_ITEMS.CUSTOM);
            }}
          />
          <Button
            flex={1}
            mb={4}
            type="button"
            onClick={() => {
              dismiss();
            }}
          >
            Cancel
          </Button>
        </Box>
      </ModalContainer>
    </DialogBox>
  );
}

Reschedule.propTypes = {
  currentClean: PropTypes.object,
  show: PropTypes.bool,
  dismiss: PropTypes.func,
  rescheduleSingleBooking: PropTypes.func,
  rescheduleRecurringBooking: PropTypes.func,
  rescheduleCustom: PropTypes.func
};

const mapStatesToProps = state => ({
  currentClean: CleansSelectors.selectCurrentClean(state)
});

const mapDispatchToProps = dispatch => ({
  rescheduleSingleBooking: ({ serviceType, addons }) => {
    dispatch(BookingActions.setBookingFrequency(FREQUENCIES.ONCE));
    dispatch(BookingActions.setBookingServiceType(serviceType));
    dispatch(BookingActions.addBookingAddon(addons));
    dispatch(BookingActions.setBookingStep(BOOKING_STAGE.SERVICE_DATE));
  },
  rescheduleRecurringBooking: ({ rows, col, serviceType, addons }) => {
    dispatch(BookingActions.setBookingFrequency(FREQUENCIES.REGULARLY));
    dispatch(
      ScheduleActions.setScheduleItemsAtOnce(rows, col, serviceType, addons)
    );

    dispatch(BookingActions.setBookingStep(BOOKING_STAGE.SERVICE_DATE));
  },
  rescheduleCustom: () => {
    dispatch(BookingActions.setBookingFrequency(FREQUENCIES.REGULARLY));
    dispatch(BookingActions.setBookingStep(BOOKING_STAGE.SERVICE_TYPE));
  }
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(Reschedule);
